import { Link } from "react-router-dom";
import { SEO } from "@/components/seo";
import { Button } from "@/components/ui/button";
import { FiDownload, FiPrinter, FiCheck, FiFileText, FiArrowRight } from "@/lib/icons";
import { broker } from "@/config/broker.config";

interface DocumentCategory {
  title: string;
  description: string;
  documents: string[];
}

const employedDocuments: DocumentCategory = {
  title: "PAYG Employed",
  description: "If you're employed full-time, part-time, or casual",
  documents: [
    "2 most recent payslips (showing YTD earnings)",
    "PAYG Payment Summary or Tax Return (last 2 years)",
    "Employment contract or letter confirming employment",
    "Group Certificate / Income Statement",
  ],
};

const selfEmployedDocuments: DocumentCategory = {
  title: "Self-Employed",
  description: "If you own a business or are a sole trader",
  documents: [
    "Last 2 years' personal Tax Returns",
    "Last 2 years' business Tax Returns (if applicable)",
    "Last 2 years' Notice of Assessment (ATO)",
    "Business Activity Statements (last 4 quarters)",
    "Accountant's letter confirming business income",
    "ABN/ACN registration certificate",
  ],
};

const identityDocuments: DocumentCategory = {
  title: "Identity Documents",
  description: "100 points of ID required",
  documents: [
    "Current Australian Driver's Licence",
    "Current Australian Passport",
    "Medicare Card",
    "Birth Certificate (Australian)",
    "Citizenship Certificate (if applicable)",
  ],
};

const assetsDocuments: DocumentCategory = {
  title: "Assets & Savings",
  description: "Evidence of your financial position",
  documents: [
    "Bank statements (all accounts, last 3 months)",
    "Term deposit statements",
    "Share portfolio statements",
    "Superannuation statements (latest)",
    "Evidence of genuine savings (3+ months history)",
    "Property valuations (if you own property)",
  ],
};

const liabilitiesDocuments: DocumentCategory = {
  title: "Liabilities & Debts",
  description: "Details of your current financial commitments",
  documents: [
    "Credit card statements (last month, all cards)",
    "Personal loan statements",
    "Car loan / lease agreements",
    "HECS/HELP debt balance (via myGov)",
    "Buy Now Pay Later statements (Afterpay, Zip, etc.)",
    "Child support / maintenance agreements",
  ],
};

const propertyDocuments: DocumentCategory = {
  title: "Property Purchase",
  description: "If you're buying a specific property",
  documents: [
    "Signed Contract of Sale",
    "Property address and details",
    "Real estate agent contact details",
    "Solicitor/Conveyancer contact details",
    "Building & pest inspection reports",
    "Strata report (if applicable)",
  ],
};

const refinanceDocuments: DocumentCategory = {
  title: "Refinancing",
  description: "If you're refinancing an existing loan",
  documents: [
    "Current home loan statement (last 6 months)",
    "Current lender details and account number",
    "Council rates notice",
    "Home insurance certificate of currency",
    "Recent property valuation (if available)",
  ],
};

const allCategories = [
  identityDocuments,
  employedDocuments,
  selfEmployedDocuments,
  assetsDocuments,
  liabilitiesDocuments,
  propertyDocuments,
  refinanceDocuments,
];

function DocumentSection({ category }: { category: DocumentCategory }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-6 print:border-gray-300 print:shadow-none">
      <h3 className="font-serif text-xl mb-2">{category.title}</h3>
      <p className="text-sm text-muted-foreground mb-4">{category.description}</p>
      <ul className="space-y-2">
        {category.documents.map((doc, index) => (
          <li key={index} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded border border-border flex-shrink-0 mt-0.5 print:border-gray-400" />
            <span className="text-sm">{doc}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DocumentChecklistPage() {
  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Trigger print which allows saving as PDF
    window.print();
  };

  return (
    <>
      <SEO
        title="Document Checklist"
        description="Complete checklist of documents needed for your home loan application. Download or print this list to prepare for your mortgage application."
        url="/guides/document-checklist"
      />

      {/* Hero - Hidden in print */}
      <section className="bg-cream py-16 md:py-20 print:hidden">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <p className="section-label mb-4">Resources</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Document Checklist
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Use this checklist to gather all the documents you'll need for your home loan
              application. Having everything ready speeds up the approval process.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button onClick={handleDownload} className="btn-pill">
                <FiDownload className="mr-2 size-4" />
                Download as PDF
              </Button>
              <Button onClick={handlePrint} variant="outline" className="btn-pill">
                <FiPrinter className="mr-2 size-4" />
                Print Checklist
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Print Header - Only visible in print */}
      <div className="hidden print:block print:mb-8">
        <h1 className="text-3xl font-bold mb-2">Home Loan Document Checklist</h1>
        <p className="text-gray-600">Path Financial Solutions | {broker.phone} | {broker.email}</p>
        <hr className="mt-4 border-gray-300" />
      </div>

      {/* Main Content */}
      <section className="py-16 md:py-20 print:py-0">
        <div className="max-w-6xl mx-auto px-4 md:px-6 print:px-0">
          {/* Quick Tips - Hidden in print */}
          <div className="bg-primary/5 rounded-2xl p-6 mb-12 print:hidden">
            <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
              <FiCheck className="size-5 text-primary" />
              Quick Tips
            </h2>
            <ul className="grid md:grid-cols-2 gap-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <FiCheck className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                Provide clear, readable copies (not blurry photos)
              </li>
              <li className="flex items-start gap-2">
                <FiCheck className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                Bank statements should show your name and account number
              </li>
              <li className="flex items-start gap-2">
                <FiCheck className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                All pages of documents must be included
              </li>
              <li className="flex items-start gap-2">
                <FiCheck className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                Recent documents (less than 30 days old) are preferred
              </li>
            </ul>
          </div>

          {/* Document Categories */}
          <div className="grid md:grid-cols-2 gap-6 print:gap-4 print:grid-cols-2">
            {allCategories.map((category) => (
              <DocumentSection key={category.title} category={category} />
            ))}
          </div>

          {/* Additional Notes */}
          <div className="mt-12 bg-muted rounded-2xl p-6 print:bg-gray-100 print:mt-8">
            <h3 className="font-serif text-xl mb-4">Additional Notes</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <FiFileText className="size-4 mt-0.5 flex-shrink-0" />
                Not all documents may be required - we'll confirm what's needed based on your situation
              </li>
              <li className="flex items-start gap-2">
                <FiFileText className="size-4 mt-0.5 flex-shrink-0" />
                Joint applications require documents from all applicants
              </li>
              <li className="flex items-start gap-2">
                <FiFileText className="size-4 mt-0.5 flex-shrink-0" />
                Some lenders may request additional documentation
              </li>
              <li className="flex items-start gap-2">
                <FiFileText className="size-4 mt-0.5 flex-shrink-0" />
                We can help you obtain documents you're unsure about
              </li>
            </ul>
          </div>

          {/* CTA - Hidden in print */}
          <div className="mt-12 text-center print:hidden">
            <p className="text-muted-foreground mb-4">
              Got your documents ready? Let's get started on your application.
            </p>
            <Button asChild className="btn-pill">
              <Link to="/contact">
                Book a Consultation
                <FiArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Print Footer */}
      <div className="hidden print:block print:mt-8 print:pt-4 print:border-t print:border-gray-300">
        <p className="text-sm text-gray-600">
          Questions? Contact {broker.name} at {broker.phone} or {broker.email}
        </p>
        <p className="text-xs text-gray-500 mt-2">
          pathfinancial.com.au | Generated {new Date().toLocaleDateString('en-AU')}
        </p>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 1.5cm;
            size: A4;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
        }
      `}</style>
    </>
  );
}
